/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { showToast } from '../../../lib/tools';
import updateReportFrame from '../../../actions/api/report/updateReportFrame';
import { REPORT_UPDATE_ERROR_CODE } from '../../../lib/constants';
import checkReportStatus from '../functions/checkReportStatus';
import getReportDetail from '../../../actions/api/report/getReportDetail';

/**
 * フレームの自動保存を行うカスタムフック。
 * @param {string} reportId - 報告書のID。
 * @param {Object} frame - フレームの情報。
 * @param {Function} setConflictFrameId - 競合しているフレームのIDを設定する関数。
 * @param {Function} updateAutoSaveStatus - 自動保存中かどうかを示す真偽値を設定する関数。プレビューボタンとステータス変更ボタンの非活性化に使用。
 * @returns {boolean} フレームが保存中かどうかを示す真偽値。
 */
const useFrameAutoSave = (
  reportId,
  frame,
  setConflictFrameId,
  updateAutoSaveStatus,
) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const apiToken = useSelector(state => state.main.apiToken);
  const targetGroupId = useSelector(state => state.main.targetGroupId);
  const targetFrame = useSelector(state =>
    state.report.reportDetail?.frames?.find(f => f.id === frame.id),
  );

  // 初回レンダーかどうかを判定するフラグ。初回レンダーでは保存処理を行わない。
  const isFirstRender = useRef(true);
  // フレーム情報を遅延させて保存するためのstate。ユーザーの入力が停止した後に保存処理を行うために使用。
  const [debouncedFrame, setDebouncedFrame] = useState(frame);

  const [isFrameSaving, setIsFrameSaving] = useState(false);

  // フレームの変更を3秒遅延させてdebouncedFrameに保存する。
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFrame(frame);
    }, 3000);
    // NOTE: 報告書件名を保存した後、自動保存中のステータスを解除する。
    updateAutoSaveStatus(`isFrameAutoSaving${frame?.id}`, false);

    return () => {
      clearTimeout(timer);
    };
  }, [frame]);

  useEffect(() => {
    if (targetFrame?.note !== frame?.note && frame?.note != null) {
      setIsFrameSaving(true);
      updateAutoSaveStatus(`isFrameAutoSaving${frame?.id}`, true);
    }
  }, [frame?.note]);

  // フレームのノートが変更された場合に自動保存処理を行う。
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    async function saveFrames() {
      if (debouncedFrame) {
        const result = await dispatch(
          updateReportFrame(
            apiToken,
            targetGroupId,
            reportId,
            frame?.id,
            {
              freeTag: debouncedFrame?.freeTag,
              note: debouncedFrame?.note,
              tags: debouncedFrame?.tags,
              workRecordId: debouncedFrame?.workRecord?.id,
            },
            frame?.updatedAt,
          ),
        );
        // 保存に成功した場合にトーストメッセージを表示する。
        if (result?.status === 200) {
          showToast(
            intl.formatMessage({ id: 'toast.message.report.autoSave' }),
          );
        }
        // ステータスを完了に変更された場合の処理
        if (
          result?.response?.status === 400 &&
          result?.response?.data?.errorCode ===
            REPORT_UPDATE_ERROR_CODE.UNEDITABLE_CONTENT
        ) {
          await checkReportStatus(apiToken, targetGroupId, reportId, dispatch);
        }
        // フレームが削除済みの場合の処理
        if (result?.response?.status === 404) {
          dispatch(getReportDetail(apiToken, targetGroupId, reportId));
        }
        // 他ユーザーと編集競合が発生した場合の処理
        if (result?.response?.status === 412) {
          setConflictFrameId(frame?.id);
        }
      }
      setIsFrameSaving(false);
      updateAutoSaveStatus(`isFrameAutoSaving${frame?.id}`, false);
    }
    // フレームの自由タグが変更された場合に保存処理を行う。
    if (targetFrame?.note !== debouncedFrame?.note) {
      saveFrames();
    }
  }, [debouncedFrame?.note]);

  return { isFrameSaving };
};

export default useFrameAutoSave;
